import i18next from 'i18next';
import Cookies from 'universal-cookie';
import { supportedLngs } from 'src/i18n';
import { setSentryUser } from 'src/sentry';
import { useState, useEffect, useCallback, createContext } from 'react';

import type { Patient, PatientProfile } from 'src/types/api';

/** Minimal shape of the non-standard `beforeinstallprompt` event. */
export interface InstallPromptEvent extends Event {
  prompt?: () => Promise<void> | void;
}

export interface UserContextValue {
  lang: string;
  setLang: (languageInput?: string) => void;
  user: Patient | undefined;
  setUser: (user?: PatientProfile | null) => void;
  installPrompt: InstallPromptEvent | undefined;
}

const UserContext = createContext<UserContextValue>({} as UserContextValue);

const roleAccess = ['patient', 'app'];

type Props = {
  children?: React.ReactNode;
};

export const UserProvider = ({ children }: Props) => {
  const [lang, setLang] = useState(i18next.language);
  const [user, setUser] = useState<Patient>();
  const [installPrompt, setInstallPrompt] = useState<InstallPromptEvent>();

  useEffect(() => {
    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    });
  }, []);

  const processUser = useCallback((passedUser?: PatientProfile | null) => {
    // attribute (or clear, on logout) Sentry events before any early return
    setSentryUser(passedUser);
    if (!passedUser) {
      setUser(undefined);
      return;
    }
    const cookies = new Cookies();
    // if there was a cached language, don't update with language from profile
    if (!cookies.get('language-cached')) modifyLanguage(passedUser.language);
    // fill displayName with whatever name parts are available
    const displayName = [passedUser.first_name, passedUser.last_name].filter(Boolean).join(' ');
    setUser({
      ...passedUser,
      ...(displayName && { displayName }),
      access: roleAccess,
    });
  }, []);

  const modifyLanguage = (languageInput?: string) => {
    if (languageInput) {
      let language: string | string[] = languageInput.split('-');
      language = language.length > 1 ? `${language[0]}-${language[1].toUpperCase()}` : language[0];

      if (supportedLngs.includes(language)) {
        i18next.changeLanguage(language);
        setLang(language);
      }
    }
  };

  return (
    <UserContext.Provider
      value={{
        lang,
        setLang: modifyLanguage,
        user,
        setUser: processUser,
        installPrompt,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export default UserContext;
